import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  fetchBookmarks,
  fetchGroup,
  getWealthMissionValueDefaults,
  getWealthMissionValues,
  postNamedCommand,
  postNamedQuery
} from '../service'
import { useAppContext } from '../redux/slices/appContext'
import { QUERY_KEYS } from './queryKeys'

export const useGroup = (groupId) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: ['group', userId, groupId],
    queryFn: async () => {
      const { data } = await fetchGroup(groupId)

      return data
    }
  })
}

export const useAdvisor = (advisorId) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: ['advisor', userId, advisorId],
    queryFn: async () => {
      const { data } = await fetchGroup(advisorId)
      return {
        ...data,
        profile: data?.extras ? JSON.parse(data.extras)?.profileData : null
      }
    }
  })
}

export const useGroupSearch = (query, options = {}) => {
  const { userId } = useAppContext()
  const { mapper = null, enabled } = options
  const queryKey = JSON.stringify(query || {})
  return useQuery({
    queryKey: [QUERY_KEYS.searchGroups, userId, queryKey],
    queryFn: async () => {
      if (!query) {
        return null
      }
      const { data } = await postNamedQuery('levels', 'searchGroups', query)
      return data
    },
    select: mapper,
    enabled
  })
}

export const useGroupTypeSearch = (query, options = {}) => {
  const { userId } = useAppContext()
  const { mapper = null } = options
  return useQuery({
    queryKey: [QUERY_KEYS.searchGroupTypes, userId, query],
    queryFn: async () => {
      if (!query) {
        return null
      }
      const { data } = await postNamedQuery(
        'levels',
        'searchGroupTypes',
        query
      )
      return data
    },
    select: mapper
  })
}

export const useBookmarksMultiple = (queries, options = {}) => {
  const { userId } = useAppContext()
  const { mapper, enabled = true } = options

  const queryMap = useMemo(() => {
    return queries.map((query) => {
      return {
        queryKey: [QUERY_KEYS.bookmarksMultiple, userId, query.levelType, query],
        queryFn: async () => {
          const { data } = await fetchBookmarks(query)
          return data
        },
        select: mapper,
        enabled
      }
    })
  }, [enabled, mapper, queries, userId])

  const results = useQueries({
    queries: queryMap
  })

  /** React Query returns a new array every render, this helps determine us memoize the result array */
  const maxResultVersion = useMemo(() => {
    return results.reduce((prev, cur) => Math.max(prev, cur.dataUpdatedAt), 0)
  }, [results])

  const [safeResult, setSafeResult] = useState(results)
  useEffect(() => {
    setSafeResult(results)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSafeResult, maxResultVersion])

  return safeResult
}

export const useBookmarks = (query, options = {}) => {
  const { userId } = useAppContext()
  const { mapper = null } = options
  return useQuery({
    queryKey: [QUERY_KEYS.bookmarks, query.levelType, userId, query],
    queryFn: async () => {
      if (!query) return null
      const { data } = await fetchBookmarks(query)
      return data
    },
    select: mapper
  })
}

export const useLeadAdvisors = (query, options = {}) => {
  const { userId } = useAppContext()
  const { mapper = null } = options
  return useQuery({
    queryKey: [QUERY_KEYS.leadAdvisors, userId, query],
    queryFn: async () => {
      if (!query) return null
      const { data } = await postNamedQuery(
        'levels',
        'searchLeadAdvisors',
        query
      )
      return data
    },
    select: mapper
  })
}

export const useLatestClientStatusReport = (clientId, options = {}, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { format = 'tree', templateId, templatePurposeKey } = options
  const useTemplateSpecificQuery = templateId || templatePurposeKey

  return useQuery({
    queryKey: [
      QUERY_KEYS.latestClientStatusReport,
      userId,
      clientId,
      format,
      templateId,
      templatePurposeKey
    ],
    enabled: !!clientId && (queryOptions.enabled !== false),
    queryFn: async () => {
      // If templateId or templatePurposeKey is provided, use the active assignment query
      if (useTemplateSpecificQuery) {
        const { data } = await postNamedQuery('levels', 'get-active-client-status-report', {
          filters: {
            levelId: clientId,
            ...(templateId && { statusTemplateId: templateId }),
            ...(templatePurposeKey && { templatePurposeKey })
          },
          includes: {
            items: true
          },
          format
        })

        // Transform response to match the expected format
        return {
          report: data?.statusReport,
          statuses: data?.statuses
        }
      }

      // Otherwise, fall back to the existing latest report query
      const { data } = await postNamedQuery('levels', 'get-latest-client-status-report', {
        clientId,
        format
      })

      return data
    },
    ...queryOptions
  })
}

export const useModifyStatusReportItemsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-status-report-items', command)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.latestClientStatusReport], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusReportAssignments], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyStatusReportItemStatusMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-status-report-item-status', command)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.latestClientStatusReport], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusReportAssignments], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useWealthMissionValueDefaults = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper, enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.wealthMissionValueDefaults, userId, query],
    queryFn: async () => {
      const { data } = await getWealthMissionValueDefaults(query)
      return data
    },
    select: mapper,
    enabled
  })
}

export const useWealthMissionValues = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper, enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.wealthMissionValues, userId, query],
    queryFn: async () => {
      const { data } = await getWealthMissionValues(query)
      return data
    },
    select: mapper,
    enabled
  })
}

export const useSearchStatusTemplates = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper, enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.searchStatusTemplates, userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'search-status-templates', query)
      return data
    },
    select: mapper,
    enabled
  })
}

export const useListClientStatusTemplateAssignments = (statusTemplateId) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.listClientStatusTemplateAssignments, userId, statusTemplateId],
    enabled: !!statusTemplateId,
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-client-status-template-assignments', {
        statusTemplateId
      })

      return data
    }
  })
}

export const useStatusTemplate = (statusTemplateId, format = 'list') => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.statusTemplate, userId, statusTemplateId, format],
    enabled: !!statusTemplateId,
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-status-template', {
        statusTemplateId,
        format
      })

      return data
    }
  })
}

export const useModifyStatusTemplateItemMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-status-template-item', command)

      return data
    },
    onSuccess: (_, command) => {
      const statusTemplateId = command.item.statusTemplateId
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusTemplate, userId, statusTemplateId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useHideStatusReportItemMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'hide-status-report-item', command)

      return data
    },
    onSuccess: (_, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.latestClientStatusReport, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusReportAssignments], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useDescribeStatusTemplateItemMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'describe-status-template-item', command)

      return data
    },
    onSuccess: (_, command) => {
      const statusTemplateId = command.item.statusTemplateId
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusTemplate, userId, statusTemplateId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useCreateStatusTemplateItemMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'create-status-template-item', command)

      return data
    },
    onSuccess: (_, command) => {
      const statusTemplateId = command.item.statusTemplateId
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusTemplate, userId, statusTemplateId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRemoveStatusTemplateItemMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'remove-status-template-item', command)

      return data
    },
    onSuccess: (_, command) => {
      const statusTemplateId = command.statusTemplateId
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusTemplate, userId, statusTemplateId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyStatusTemplateDetailsMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-status-template-details', command)

      return data
    },
    onSuccess: (_, command) => {
      const statusTemplateId = command.statusTemplateId
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusTemplate, userId, statusTemplateId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useCreateStatusTemplateMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'create-status-template', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchStatusTemplates], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRunClientStatusReportMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'run-client-status-report', command)

      return data
    },
    onSuccess: (data, command) => {
      // Invalidate status report assignments to refresh the active report
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.latestClientStatusReport], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusReportAssignments], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useListAllKeyDatapoints = (queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.listAllKeyDatapoints, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-all-datapoints', {})
      return data
    },
    enabled
  })
}
export const useGetDatapointDetails = (code, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.getDatapointDetails, userId, code],
    queryFn: async () => {
      if (code === 'new') {
        return { data: {} }
      }
      const { data } = await postNamedQuery('levels', 'get-datapoint-details', {
        code
      })
      return data
    },
    enabled
  })
}

export const useListClientKeyDatapoints = (clientId, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.listClientKeyDatapoints, userId, clientId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-client-key-datapoints', {
        clientId
      })
      return data
    },
    enabled
  })
}

export const useGetClientDatapointSchema = (queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.getClientDatapointSchema, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-client-datapoint-schema', {
        options: {
          includeHidden: true,
          includeAutomated: true
        }
      })
      return data
    },
    enabled
  })
}

export const useGetClientDatapointValues = (clientId, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.getClientDatapointValues, userId, clientId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-client-datapoint-values', {
        clientId,
        options: {
          includeHidden: true,
          includeAutomated: true
        }
      })
      return data
    },
    enabled
  })
}

export const useModifyClientKeyDatapointValues = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-client-key-datapoint-values', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listClientKeyDatapoints, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getClientDatapointValues, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyDatapointDetails = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-datapoint-details', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listAllKeyDatapoints, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getDatapointDetails, userId, command.code], refetchType: 'all' }).catch(console.error)
    }
  })
}

/* Account Key Datapoints */
export const useListAccountKeyDatapoints = (accountId, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.listAccountKeyDatapoints, userId, accountId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-account-key-datapoints', {
        accountId
      })
      return data
    },
    enabled
  })
}

export const useGetAccountDatapointSchema = (queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.getAccountDatapointSchema, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-account-datapoint-schema', {
        options: {
          includeHidden: true,
          includeAutomated: true
        }
      })
      return data
    },
    enabled
  })
}

export const useGetAccountDatapointValues = (accountId, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.getAccountDatapointValues, userId, accountId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-account-datapoint-values', {
        accountId,
        options: {
          includeHidden: true,
          includeAutomated: true
        }
      })
      return data
    },
    enabled
  })
}

export const useModifyAccountKeyDatapointValues = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-account-key-datapoint-values', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listAccountKeyDatapoints, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getAccountDatapointValues, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useGetLevelDatapointSchema = (levelTypeId, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.getLevelDatapointSchema, userId, levelTypeId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-level-datapoint-schema', {
        levelTypeId,
        options: {
          includeHidden: true,
          includeAutomated: true
        }
      })
      return data
    },
    enabled
  })
}

export const useGetLevelDatapointValues = (levelTypeId, levelId, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.getLevelDatapointValues, userId, levelTypeId, levelId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-level-datapoint-values', {
        levelTypeId,
        levelId,
        options: {
          includeHidden: true,
          includeAutomated: true
        }
      })
      return data
    },
    enabled
  })
}

export const useModifyLevelKeyDatapointValues = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-level-key-datapoint-values', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getLevelDatapointValues, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useClientStatusTemplateAssignment = (clientId, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.getClientStatusTemplateAssignment, userId, clientId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-client-status-template-assignment', {
        clientId
      })

      return data
    },
    enabled
  })
}

export const useAssignClientStatusTemplate = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'assign-client-status-template', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchStatusTemplates, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getClientStatusTemplateAssignment, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyClientAccountOrdinals = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-client-account-ordinals', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchAccountsMultiple, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRoleTypes = () => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.roleTypes, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-role-types', {})
      return data?.roleTypes
    },
    enabled: !!userId
  })
}

export const useAdvisorTeamMember = (userId) => {
  const { userId: caller } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.getAdvisorTeamMember, caller, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-advisor-team-member', {
        userId
      })

      return data
    }
  })
}

export const useTeamMemberSchema = () => {
  const { userId: caller } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.getTeamMemberSchema, caller],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-team-member-attributes', {
      })

      return data
    }
  })
}

export const useModifyAdvisorTeamMember = () => {
  const queryClient = useQueryClient()
  const { userId: caller } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-advisor-team-member', command)

      return data
    },
    onSuccess: (data, variables) => {
      const { userId } = variables
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getAdvisorTeamMember, caller, userId], exact: true }).catch(console.error)
    }
  })
}

export const useListClientAccountGroupSchema = (clientId, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.listClientAccountGroupSchema, userId, clientId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-client-account-group-schema', {
        clientId
      })
      return data
    },
    enabled
  })
}

export const useGetClientAccountGroupHierarchy = (clientId, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.getClientAccountGroupHierarchy, userId, clientId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-client-account-group-hierarchy', {
        clientId
      })
      return data
    },
    enabled
  })
}

export const useCreateClientAccountGroup = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'create-client-account-group', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getClientAccountGroupHierarchy, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listClientAccountGroupSchema, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchGroups, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useAssignClientAccountGroup = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'assign-client-account-group', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getClientAccountGroupHierarchy, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listClientAccountGroupSchema, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchGroups, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useAssignClientAccountGroupAccounts = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'assign-client-account-group-accounts', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getClientAccountGroupHierarchy, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listClientAccountGroupSchema, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchGroups, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRenameClientAccountGroup = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'rename-client-account-group', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getClientAccountGroupHierarchy, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.listClientAccountGroupSchema, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchGroups, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useAssignClientGroupMembership = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'assign-client-group-membership', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchGroups, userId], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchGroupTypes, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyClientRestriction = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'modify-client-restriction', command)

      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchClients, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useMyAdvisor = () => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.myAdvisor, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-my-advisor', {})
      return data
    },
    enabled: !!userId
  })
}

export const useSearchAssets = (query, options = {}) => {
  const { enabled = true, mapper } = options
  const queryKey = query ? JSON.stringify(query) : null
  return useQuery({
    enabled,
    queryKey: ['levels', QUERY_KEYS.searchAssets, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'search-assets', query)
      return data
    },
    select: mapper
  })
}

export const useAssignAccountClients = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'assign-account-clients', command)
      return data
    },
    onSuccess: async (data, command) => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchClientsMultiple], refetchType: 'all' })
    }
  })
}

export const useFinalizeClientStatusReport = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'finalize-client-status-report', command)
      return data
    },
    onSuccess: (data, command) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.latestClientStatusReport], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusReportAssignments], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useArchiveClientStatusReport = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'archive-client-status-report', command)
      return data
    },
    onSuccess: (data, command) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.latestClientStatusReport], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusReportAssignments], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useListClientStatusReports = (query, queryOptions = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.statusReportAssignments, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-client-status-reports', query)
      return data
    },
    ...queryOptions
  })
}

export const useGetActiveClientStatusReport = (query, queryOptions = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.statusReportAssignments, 'activeAssignment', query],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-active-client-status-report', query)
      return data
    },
    ...queryOptions
  })
}

export const useGetStatusReportSummary = (query, queryOptions = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.statusReportSummaries, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'generate-status-report-summary', query)
      return data
    },
    ...queryOptions
  })
}

export const useGetStatusReportSummaryConfigs = (query = {}, queryOptions = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.statusReportSummaryConfigs, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-status-report-summary-configs', query)
      return data
    },
    ...queryOptions
  })
}

export const useGetStatusReportById = (statusReportId, options = {}, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { format = 'tree' } = options

  return useQuery({
    queryKey: [QUERY_KEYS.statusReportAssignments, 'byId', userId, statusReportId, format],
    enabled: !!statusReportId,
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-client-status-reports', {
        filters: {
          statusReportId
        },
        includes: {
          items: true
        },
        format
      })

      // Transform response to match the expected format
      const statusReport = data?.statusReports?.[0]
      return {
        report: statusReport,
        statuses: data?.statuses
      }
    },
    ...queryOptions
  })
}

// Metadata Types Hooks
export const useListStatusTemplateMetadataTypes = (statusTemplateId, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.statusTemplateMetadataTypes, userId, statusTemplateId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-status-template-metadata-types', {
        statusTemplateId
      })
      return data
    },
    enabled: enabled && !!statusTemplateId
  })
}

export const useCreateStatusTemplateMetadataTypeMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'create-status-template-metadata-type', command)
      return data
    },
    onSuccess: (_, command) => {
      const statusTemplateId = command.statusTemplateId
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusTemplateMetadataTypes, userId, statusTemplateId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useUpdateStatusTemplateMetadataTypeMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'update-status-template-metadata-type', command)
      return data
    },
    onSuccess: (_, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusTemplateMetadataTypes, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useDeleteStatusTemplateMetadataTypeMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'delete-status-template-metadata-type', command)
      return data
    },
    onSuccess: (_, command) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusTemplateMetadataTypes, userId], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useUpdateStatusReportItemMetadataMutation = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()

  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('levels', 'update-status-report-item-metadata', command)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.latestClientStatusReport, userId],
        refetchType: 'all'
      }).catch(console.error)

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.statusReportAssignments],
        refetchType: 'all'
      }).catch(console.error)
    }
  })
}

export const useListStatusReportStatuses = (queryOptions = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.statusReportStatuses],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-status-report-statuses', {})
      return data?.statuses || []
    },
    ...queryOptions
  })
}

export const useGetStatusReportStatusesImagePurpose = () => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.statusReportStatuses, userId, 'image-purpose-id'],
    queryFn: async () => {
      const { data } = await postNamedQuery('media', 'list-image-purposes', {})
      const statusReportLevel = data?.purposes?.find(p => p.levelTypeId === 279)
      return statusReportLevel?.purposes?.find(p => p.code === 'statusReportStatus') || null
    },
    enabled: !!userId
  })
}

export const useStatusIcon = (statusId, options = {}) => {
  const purpose = useGetStatusReportStatusesImagePurpose()

  const query = purpose && statusId ? {
    purpose: {
      purposeCode: 'statusReportStatus',
      levelTypeId: 279,
      levelId: statusId
    },
    take: 1
  } : null

  return useQuery({
    enabled: (options?.enabled ?? true) && !!query,
    queryKey: [QUERY_KEYS.statusReportStatuses, statusId],
    keepPreviousData: true,
    queryFn: async () => {
      const { data } = await postNamedQuery('media', 'list-images', query)
      return data?.data?.[0] || null
    }
  })
}

export const useUpdateStatusReportStatusMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ statusId, displayName, color, codeName }) => {
      const { data } = await postNamedCommand('levels', 'update-status-report-status', {
        statusId,
        displayName,
        color,
        codeName
      })
      return data
    },
    onSuccess: ({ statusId }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusReportStatuses] })
    }
  })
}

export const useCreateStatusReportStatusMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ displayName, color, codeName, icon }) => {
      const { data } = await postNamedCommand('levels', 'create-status-report-status', {
        displayName,
        color,
        codeName,
        icon
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusReportStatuses] })
    }
  })
}
